import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Package,
  ArrowLeft,
  Send,
  Mail,
  CheckCircle,
  User,
  Briefcase,
  Plane,
  Clock,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import {
  useJobPacket,
  useSendJobPacket,
  useSendJobPacketEmail,
  useAcknowledgeJobPacket,
} from '@/hooks/use-job-packets';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateOnly, formatDateTimeInTimeZone } from '@/lib/date-utils';
import type { JobPacketStatus } from '@/lib/types';

const statusColors: Record<JobPacketStatus, string> = {
  draft: 'bg-gray-100 text-gray-800',
  sent: 'bg-blue-100 text-blue-800',
  viewed: 'bg-yellow-100 text-yellow-800',
  acknowledged: 'bg-green-100 text-green-800',
};

export function JobPacketDetailPage() {
  const timeZone = useOrgTimeZone();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const packetId = Number(id);

  const { data: packet, isLoading, error } = useJobPacket(packetId);
  const sendH3Kit = useSendJobPacket();
  const sendEmail = useSendJobPacketEmail();
  const acknowledge = useAcknowledgeJobPacket();

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <p className="text-gray-500">Loading job packet...</p>
      </div>
    );
  }

  if (error || !packet) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <p className="text-red-500">Failed to load job packet.</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/crew/job-packets')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Job Packets
        </Button>
      </div>
    );
  }

  const tech = packet.tech_detail;
  const assignment = packet.assignment_detail;
  const hasH3Kit = tech?.h3kit_user_id != null;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <Button variant="ghost" size="sm" asChild className="mb-2">
            <Link to="/crew/job-packets">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Job Packets
            </Link>
          </Button>
          <div className="flex items-center gap-3">
            <Package className="h-7 w-7 text-blue-600" />
            <h1 className="text-2xl font-bold text-gray-900">
              Job Packet #{packet.id}
            </h1>
            <Badge className={statusColors[packet.status]}>
              {packet.status}
            </Badge>
          </div>
          <p className="text-gray-600 mt-1">
            Created by {packet.created_by_username} on{' '}
            {formatDateTimeInTimeZone(packet.created_at, timeZone, { dateStyle: 'medium' })}
          </p>
        </div>

        {/* Action buttons */}
        <div className="flex gap-2">
          {packet.status === 'draft' && (
            <>
              <Button
                onClick={() => sendH3Kit.mutate(packetId)}
                disabled={sendH3Kit.isPending}
              >
                <Send className="h-4 w-4 mr-2" />
                {sendH3Kit.isPending ? 'Sending...' : 'Send to H3Kit'}
              </Button>
              <Button
                variant="outline"
                onClick={() => sendEmail.mutate(packetId)}
                disabled={sendEmail.isPending}
              >
                <Mail className="h-4 w-4 mr-2" />
                {sendEmail.isPending ? 'Sending...' : 'Send via Email'}
              </Button>
            </>
          )}
          {(packet.status === 'sent' || packet.status === 'viewed') && (
            <Button
              variant="outline"
              onClick={() => acknowledge.mutate(packetId)}
              disabled={acknowledge.isPending}
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              {acknowledge.isPending ? 'Processing...' : 'Mark Acknowledged'}
            </Button>
          )}
        </div>
      </div>

      {/* Status Timeline */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Status Timeline
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-500">Created</p>
              <p className="font-medium">
                {formatDateTimeInTimeZone(packet.created_at, timeZone)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Sent</p>
              <p className="font-medium">
                {packet.sent_at
                  ? formatDateTimeInTimeZone(packet.sent_at, timeZone)
                  : '—'}
              </p>
              {packet.sent_via && (
                <p className="text-xs text-gray-400">via {packet.sent_via}</p>
              )}
            </div>
            <div>
              <p className="text-sm text-gray-500">Viewed</p>
              <p className="font-medium">
                {packet.viewed_at
                  ? formatDateTimeInTimeZone(packet.viewed_at, timeZone)
                  : '—'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Acknowledged</p>
              <p className="font-medium">
                {packet.acknowledged_at
                  ? formatDateTimeInTimeZone(packet.acknowledged_at, timeZone)
                  : '—'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notes for Tech */}
      {packet.notes_for_tech && (
        <Card>
          <CardHeader>
            <CardTitle>Notes for Tech</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 whitespace-pre-wrap">{packet.notes_for_tech}</p>
          </CardContent>
        </Card>
      )}

      {/* Tech Profile */}
      {tech && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Tech Profile
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <Link
                  to={`/crew/techs/${tech.id}`}
                  className="font-medium text-blue-600 hover:underline"
                >
                  {tech.first_name} {tech.last_name}
                </Link>
              </div>
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium">{tech.email}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Phone</p>
                <p className="font-medium">{tech.phone || '—'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Job Title</p>
                <p className="font-medium">{tech.job_title || '—'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">H3Kit</p>
                <p className="font-medium">
                  {hasH3Kit ? (
                    <Badge className="bg-green-100 text-green-800">Connected</Badge>
                  ) : (
                    <Badge className="bg-gray-100 text-gray-800">Not Connected</Badge>
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Assignment Details */}
      {assignment && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              Assignment Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-500">Project</p>
                <p className="font-medium">{assignment.project_name}</p>
              </div>
              {assignment.project_number && (
                <div>
                  <p className="text-sm text-gray-500">Project Number</p>
                  <p className="font-medium">{assignment.project_number}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-500">Role</p>
                <p className="font-medium">{assignment.role || '—'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Start Date</p>
                <p className="font-medium">{formatDateOnly(assignment.start_date)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">End Date</p>
                <p className="font-medium">{formatDateOnly(assignment.end_date)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Location</p>
                <p className="font-medium">{assignment.location || '—'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <Badge variant="outline">{assignment.status}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Travel Records */}
      {assignment?.travel_records && assignment.travel_records.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plane className="h-5 w-5" />
              Travel Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {assignment.travel_records.map((tr) => (
                <div
                  key={tr.id}
                  className="p-3 border rounded-lg"
                >
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant="outline">{tr.travel_type}</Badge>
                    {tr.confirmation_number && (
                      <span className="text-sm text-gray-500">
                        Conf: {tr.confirmation_number}
                      </span>
                    )}
                  </div>
                  <p className="font-medium">{tr.description}</p>
                  <p className="text-sm text-gray-600">
                    {formatDateOnly(tr.departure_date)}
                    {tr.return_date ? ` — ${formatDateOnly(tr.return_date)}` : ''}
                  </p>
                  {tr.flight_info && (
                    <p className="text-sm text-gray-500 mt-1">
                      {tr.flight_info.airline} {tr.flight_info.flight_number}:{' '}
                      {tr.flight_info.departure_airport} → {tr.flight_info.arrival_airport}
                    </p>
                  )}
                  {tr.hotel_booking && (
                    <p className="text-sm text-gray-500 mt-1">
                      {tr.hotel_booking.hotel_name} — Check-in:{' '}
                      {formatDateOnly(tr.hotel_booking.check_in_date)}
                    </p>
                  )}
                  {tr.car_rental && (
                    <p className="text-sm text-gray-500 mt-1">
                      {tr.car_rental.rental_company} — Pickup:{' '}
                      {formatDateOnly(tr.car_rental.pickup_date)}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
